// © Microsoft Corporation. All rights reserved.

import { AccentButton, OutlineButton } from "@microsoft/fast-components-react-msft";
import { classNames } from "@microsoft/fast-web-utilities";
import { User } from "@msnews/social-data-service";
import { enums, hasThemeBase, addOrUpdateTelemetryObject, renderHybrid } from "@msnews/social-utils";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";
import React from "react";
import { connect } from "react-redux";
import { SocialCommentClassNameContract } from "../SocialComment.classnames";
import { SocialCommentLocalizedStrings } from "../SocialComment.localizedStrings";
import { FollowInfo, UsersConnector } from "../SocialComment.props";
import { SocialCommentState } from "../SocialComment.state";
import { followButtonTelemetryActionDispatchers, followButtonTelemetryContracts, FollowButtonTelemetryObjects } from "./FollowButton.telemetry";

export interface FollowButtonProps {
    me: User,
    userId: string,
    users: { [id: string]: FollowInfo;}, // dictionary definening
    managedClasses: SocialCommentClassNameContract,
    usersConnector: UsersConnector,
    onClick?: (e, userid: string, isFollow: boolean) => void,
    mainTelemetryObject?: TelemetryObjectBase
    t: SocialCommentLocalizedStrings
    theme: string
}

const mapStateToProps = function(state: SocialCommentState) {
    return {
        me: state.me,
        users: state.users,
        theme: state.theme
    };
};

export class FollowButton extends React.Component<FollowButtonProps> {
    telemetryObjects: FollowButtonTelemetryObjects;

    componentDidMount() {
        if (!(this.props.users && this.props.users[this.props.userId])) {
            const fetchUser = this.props.usersConnector.fetchUser;
            const fetchIsUserFollowed = this.props.usersConnector.fetchIsUserFollowed;
            if (!fetchUser || !fetchIsUserFollowed) { return console.warn("Missing props 'usersConnector.fetchUser' or 'userConnector.fetchIsUserFollowed'"); }
            fetchUser(this.props.userId)
                .then(_ => {
                    fetchIsUserFollowed(this.props.userId);
                });
        }
    }

    render() {
        const {me, users, userId, managedClasses, mainTelemetryObject, t, theme} = this.props;
        const isSuperApp = hasThemeBase(theme, enums.theme.superappBase);

        const user = users[userId] ? users[userId].data : null;
        if (!user) { return null; }
        const {isFollowing, isPrivateProfile, traceId} = user;
        const isSelf = me && me.id == userId;
        const isAnonymous = user.state == enums.userStateType.anonymous;

        /** Generate Telemetry Objects */
        if (mainTelemetryObject) {
            this.telemetryObjects = {
                followButton: addOrUpdateTelemetryObject(mainTelemetryObject, {...followButtonTelemetryContracts.followButton}, { id: traceId, headline: !isFollowing ? "Follow" : "Unfollow"}, this.telemetryObjects && this.telemetryObjects.followButton)
            };
        }

        const FollowButtonWrapperComponent = isFollowing ? OutlineButton :AccentButton;

        return (
            <React.Fragment>
                {!isSelf && !isPrivateProfile && !isAnonymous &&
                <FollowButtonWrapperComponent
                    key={isFollowing ? `unfollow-${me.id}-${userId}` : `follow-${me.id}-${userId}`}
                    aria-live="assertive"
                    type="button"
                    name={t.FollowButtonTitle}
                    className={classNames(
                        managedClasses.followButtonContainer,
                        [managedClasses.followButtonSuperAppContainer, isSuperApp],
                        "cwt-fast-button"
                    )}
                    data-is-followed={isFollowing}
                    onClick={(e) => this.toggleFollow(e, userId, isFollowing)}
                    {...renderHybrid(this.telemetryObjects && this.telemetryObjects.followButton, false)}
                >
                    {isFollowing ? t.FollowButtonFollowingText :t.FollowButtonFollowText}
                </FollowButtonWrapperComponent> }
            </React.Fragment>
        );
    }

    toggleFollow(e, userId, isFollowing) {
        if (this.props.onClick) { this.props.onClick(e, userId, !isFollowing); }
        e.stopPropagation();

        this.props.usersConnector.followUser(userId, !isFollowing);
        followButtonTelemetryActionDispatchers.followButton(e.currentTarget, !isFollowing, this.telemetryObjects.followButton);
    }
}

export default connect(mapStateToProps)(FollowButton);